import { useState, useEffect, useRef } from 'react';
import React from 'react';
import axios from 'axios';

interface TenantInfo {
  tenantName: string;
  tenantLogo: string;
  tenantPrivacyUrl: string;
  tenantTermUrl: string;
  tenantFacebookUrl: string;
  tenantXUrl: string;
  tenantLinkedinUrl: string;
  tenantSiteLanguages: string[];
}

// Default logo fallback path
const DEFAULT_LOGO_PATH = '/static/images/new-rec-logos.svg';

// Helper function to get meta tag content - handles multiple meta tags with same name
const getMetaContent = (metaName: string): string => {
  // Try to get all meta tags with this name
  const metaTags = document.querySelectorAll(`meta[name="${metaName}"]`);

  if (!metaTags || metaTags.length === 0) {
    return '';
  }

  // If multiple meta tags exist, prioritize the one with non-empty content
  for (let i = 0; i < metaTags.length; i++) {
    const content = metaTags[i].getAttribute('content');
    if (content && content.trim() !== '') {
      return content;
    }
  }

  // If all are empty or no content attribute, return empty string
  return metaTags[0].getAttribute('content') || '';
};

// Parse languages string to array
const parseLanguages = (languagesStr: string): string[] => {
  if (!languagesStr || languagesStr.trim() === '') {
    return [];
  }
  const result = languagesStr.split(',').map((lang) => lang.trim());
  return result;
};

// Fetch tenant info from API
const fetchTenantFromApi = async (
  setStateFn?: React.Dispatch<React.SetStateAction<TenantInfo>>
): Promise<TenantInfo | null> => {
  try {
    // Try different API endpoints - attempt with both relative and absolute paths
    let response;
    let apiError = null;

    try {
      // First try the relative path
      response = await axios.get('/api/tenant', { timeout: 5000 });
    } catch (err) {
      apiError = err;
      try {
        // If that fails, try with the full URL
        const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
        response = await axios.get(`${baseUrl}/api/tenant`, { timeout: 5000 });
      } catch (err2) {
        console.error(
          'Failed to fetch tenant info from both relative and absolute URLs',
          apiError,
          err2
        );
        return null;
      }
    }

    if (response?.data && response.data.length > 0) {
      const tenant = response.data[0];

      const parsedLanguages = parseLanguages(tenant.site_languages || '');

      // If a state setter function was provided, update state with API data
      // but preserve any existing languages if API returns empty
      if (setStateFn) {
        setStateFn((prevState: TenantInfo) => {
          const tenantInfo: TenantInfo = {
            tenantName: tenant.name || prevState.tenantName || 'Skillio',
            tenantLogo:
              tenant.logo || prevState.tenantLogo || DEFAULT_LOGO_PATH,
            tenantPrivacyUrl:
              tenant.privacy_statement || prevState.tenantPrivacyUrl || '',
            tenantTermUrl:
              tenant.general_terms || prevState.tenantTermUrl || '',
            tenantFacebookUrl:
              (tenant.social_media_links &&
                tenant.social_media_links.facebook) ||
              prevState.tenantFacebookUrl ||
              '',
            tenantXUrl:
              (tenant.social_media_links &&
                tenant.social_media_links.twitter) ||
              prevState.tenantXUrl ||
              '',
            tenantLinkedinUrl:
              (tenant.social_media_links &&
                tenant.social_media_links.linkedin) ||
              prevState.tenantLinkedinUrl ||
              '',
            // Preserve existing languages if API returns empty
            tenantSiteLanguages:
              parsedLanguages.length > 0
                ? parsedLanguages
                : prevState.tenantSiteLanguages
          };
          return tenantInfo;
        });
      }

      // Update meta tags if tenant info is found
      if (tenant.name) {
        document
          .querySelector('meta[name="tenant-name"]')
          ?.setAttribute('content', tenant.name);
      }

      if (tenant.logo) {
        document
          .querySelector('meta[name="tenant-logo"]')
          ?.setAttribute('content', tenant.logo);
      }

      if (tenant.privacy_statement) {
        document
          .querySelector('meta[name="tenant-privacy-url"]')
          ?.setAttribute('content', tenant.privacy_statement);
      }

      if (tenant.general_terms) {
        document
          .querySelector('meta[name="tenant-term-url"]')
          ?.setAttribute('content', tenant.general_terms);
      }

      if (tenant.social_media_links?.facebook) {
        document
          .querySelector('meta[name="facebook-url"]')
          ?.setAttribute('content', tenant.social_media_links.facebook);
      }

      if (tenant.social_media_links?.twitter) {
        document
          .querySelector('meta[name="x-url"]')
          ?.setAttribute('content', tenant.social_media_links.twitter);
      }

      if (tenant.social_media_links?.linkedin) {
        document
          .querySelector('meta[name="linkedin-url"]')
          ?.setAttribute('content', tenant.social_media_links.linkedin);
      }

      if (tenant.primary_color) {
        document
          .querySelector('meta[name="tenant-color"]')
          ?.setAttribute('content', tenant.primary_color);

        // Also update theme-color meta tag for browsers
        document
          .querySelector('meta[name="theme-color"]')
          ?.setAttribute('content', tenant.primary_color);
      }

      // Return tenant info for API call
      return {
        tenantName: tenant.name || 'Skillio',
        tenantLogo: tenant.logo || DEFAULT_LOGO_PATH,
        tenantPrivacyUrl: tenant.privacy_statement || '',
        tenantTermUrl: tenant.general_terms || '',
        tenantFacebookUrl:
          (tenant.social_media_links && tenant.social_media_links.facebook) ||
          '',
        tenantXUrl:
          (tenant.social_media_links && tenant.social_media_links.twitter) ||
          '',
        tenantLinkedinUrl:
          (tenant.social_media_links && tenant.social_media_links.linkedin) ||
          '',
        tenantSiteLanguages: parsedLanguages
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching tenant info from API:', error);
    return null;
  }
};

export const useTenantInfo = (): TenantInfo => {
  // We need to track if we already have a valid logo to prevent flicker
  const hasValidLogo = useRef<boolean>(false);

  const [tenantInfo, setTenantInfo] = useState<TenantInfo>({
    tenantName: 'Skillio',
    tenantLogo: '',
    tenantPrivacyUrl: '',
    tenantTermUrl: '',
    tenantFacebookUrl: '',
    tenantXUrl: '',
    tenantLinkedinUrl: '',
    tenantSiteLanguages: []
  });

  const win = typeof window !== 'undefined';

  // Function to update tenant info from meta tags
  const getTenantInfoFromMetaTags = () => {
    // Get values from meta tags using the helper function
    const tenantName = getMetaContent('tenant-name');
    const tenantLogo = getMetaContent('tenant-logo');
    const tenantPrivacyUrl = getMetaContent('tenant-privacy-url');
    const tenantTermUrl = getMetaContent('tenant-term-url');
    const tenantFacebookUrl = getMetaContent('facebook-url');
    const tenantXUrl = getMetaContent('x-url');
    const tenantLinkedinUrl = getMetaContent('linkedin-url');
    const tenantSiteLanguagesStr = getMetaContent('tenant-site-languages');

    const tenantSiteLanguages = parseLanguages(tenantSiteLanguagesStr);

    // Check if we found a valid logo
    if (tenantLogo && tenantLogo.trim() !== '') {
      hasValidLogo.current = true;
    }

    // Only update state if we have actual content to avoid unnecessary renders
    setTenantInfo((prevState) => {
      // If we already have a valid logo, don't replace it with default
      const logoToUse =
        hasValidLogo.current && prevState.tenantLogo
          ? prevState.tenantLogo
          : tenantLogo && tenantLogo.trim() !== ''
            ? tenantLogo
            : prevState.tenantLogo || '';

      return {
        ...prevState,
        tenantName: tenantName || prevState.tenantName,
        tenantLogo: logoToUse,
        tenantPrivacyUrl: tenantPrivacyUrl || prevState.tenantPrivacyUrl,
        tenantTermUrl: tenantTermUrl || prevState.tenantTermUrl,
        tenantFacebookUrl: tenantFacebookUrl || prevState.tenantFacebookUrl,
        tenantXUrl: tenantXUrl || prevState.tenantXUrl,
        tenantLinkedinUrl: tenantLinkedinUrl || prevState.tenantLinkedinUrl,
        tenantSiteLanguages:
          tenantSiteLanguages.length > 0
            ? tenantSiteLanguages
            : prevState.tenantSiteLanguages
      };
    });

    // Return true if we had logo content from meta tag
    return tenantLogo && tenantLogo.trim() !== '';
  };

  useEffect(() => {
    if (win) {
      // First, try to get info from meta tags
      const metaTagsHadLogo = getTenantInfoFromMetaTags();

      // If we got a logo from meta tags, mark it as valid
      if (metaTagsHadLogo) {
        hasValidLogo.current = true;
      }

      // If we don't have a valid logo yet, try API
      if (!hasValidLogo.current) {
        // Fetch from API if no logo was found in meta tags
        fetchTenantFromApi(setTenantInfo).then((apiTenantInfo) => {
          if (apiTenantInfo && apiTenantInfo.tenantLogo) {
            // If we got a logo from API, mark it as valid
            hasValidLogo.current = true;
          } else if (!hasValidLogo.current) {
            // If no logo from API or meta tags, use default
            setTenantInfo((prevState) => ({
              ...prevState,
              tenantLogo: DEFAULT_LOGO_PATH
            }));
          }
        });
      }
    }
  }, [win]);

  // Set default logo if still empty after a delay
  useEffect(() => {
    if (!win) return;

    // If we still don't have a logo after all attempts, use default
    if (!tenantInfo.tenantLogo) {
      const timer = setTimeout(() => {
        setTenantInfo((prevState) => {
          // Only set default if it's still empty
          if (!prevState.tenantLogo) {
            return {
              ...prevState,
              tenantLogo: DEFAULT_LOGO_PATH
            };
          }
          return prevState;
        });
      }, 500); // Reduced wait time for better user experience

      return () => clearTimeout(timer);
    }
  }, [win, tenantInfo.tenantLogo]);

  return tenantInfo;
};
